import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export function Layout({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileMenuOpen(false);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = searchQuery.trim();
    navigate(q ? `/products?search=${encodeURIComponent(q)}` : '/products');
    setMobileMenuOpen(false);
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const searchForm = (
    <form
      onSubmit={handleSearchSubmit}
      className="flex min-w-0 rounded-lg border border-slate-200 bg-slate-50/80 overflow-hidden focus-within:ring-2 focus-within:ring-amber-500/50 focus-within:border-amber-500/50 transition w-full md:max-w-[220px] md:ml-2"
    >
      <label htmlFor="header-product-search" className="sr-only">Search products</label>
      <span className="pointer-events-none flex items-center pl-2.5 md:pl-3 text-slate-400">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </span>
      <input
        id="header-product-search"
        type="search"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="Search products…"
        className="flex-1 min-w-0 bg-transparent py-2 md:py-2 pl-2 pr-2 text-slate-800 text-sm placeholder-slate-400 focus:outline-none"
      />
      <button
        type="submit"
        className="flex-shrink-0 px-3 py-2 bg-amber-500 text-white text-sm font-medium hover:bg-amber-400 transition"
      >
        Search
      </button>
    </form>
  );

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="sticky top-0 z-50 w-full bg-white shadow-sm">
        {/* Top row: logo + nav + search + auth */}
        <div className="border-b border-slate-200/80 bg-white">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="h-14 md:h-16 flex items-center justify-between gap-2 sm:gap-4">
              <Link to="/" className="flex items-center flex-shrink-0" onClick={closeMobileMenu}>
                <img src="/images.png" alt="AG Cash & Carry" className="h-12 sm:h-14 md:h-16 w-auto" />
              </Link>

              {/* AG CASH & CARRY: between logo and nav */}
              <div className="flex min-w-0 items-center justify-center md:justify-start px-2 sm:px-4">
                <Link to="/" onClick={closeMobileMenu} className="text-slate-900 font-bold tracking-wide whitespace-nowrap truncate text-xs sm:text-sm md:text-base lg:text-lg">
                  AG CASH & CARRY
                </Link>
              </div>

              {/* Desktop nav */}
              <nav className="hidden md:flex items-center gap-0.5 lg:gap-1 flex-shrink-0 justify-end">
                <Link to="/" className="px-3 py-2.5 text-slate-600 hover:text-slate-900 hover:bg-slate-50 font-medium rounded-lg transition text-sm">
                  Home
                </Link>
                <Link to="/about-us" className="px-3 py-2.5 text-slate-600 hover:text-slate-900 hover:bg-slate-50 font-medium rounded-lg transition text-sm">
                  About Us
                </Link>
                <Link to="/products" className="px-3 py-2.5 text-slate-600 hover:text-slate-900 hover:bg-slate-50 font-medium rounded-lg transition text-sm">
                  Products
                </Link>
                <span className="w-px h-6 bg-slate-200 mx-1 lg:mx-2 flex-shrink-0" aria-hidden />
                {searchForm}
                <span className="w-px h-6 bg-slate-200 ml-1 lg:ml-2 flex-shrink-0" aria-hidden />
                {isAuthenticated ? (
                  <>
                    <span className="hidden lg:inline text-slate-500 text-sm truncate max-w-[140px] pl-1">
                      {user?.name || user?.email}
                    </span>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login" className="px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition">
                      Login
                    </Link>
                    <Link to="/register" className="px-3 py-2.5 text-sm font-medium bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition ml-0.5">
                      Sign up
                    </Link>
                  </>
                )}
              </nav>

              {/* Mobile: hamburger (AG CASH & CARRY already shown in middle above) */}
              <button
                type="button"
                onClick={() => setMobileMenuOpen((o) => !o)}
                className="md:hidden flex-shrink-0 p-2.5 rounded-lg text-slate-600 hover:bg-slate-100 transition"
                aria-expanded={mobileMenuOpen}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          <div
            className={`md:hidden overflow-hidden transition-all duration-200 ease-out ${
              mobileMenuOpen ? 'max-h-[80vh] opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="py-4 pb-6 px-4 border-t border-slate-200/80 space-y-1">
              <Link to="/" onClick={closeMobileMenu} className="block px-3 py-2.5 text-slate-700 font-medium rounded-lg hover:bg-slate-50">
                Home
              </Link>
              <Link to="/about-us" onClick={closeMobileMenu} className="block px-3 py-2.5 text-slate-700 font-medium rounded-lg hover:bg-slate-50">
                About Us
              </Link>
              <Link to="/products" onClick={closeMobileMenu} className="block px-3 py-2.5 text-slate-700 font-medium rounded-lg hover:bg-slate-50">
                Products
              </Link>
              <div className="pt-3 pb-2">{searchForm}</div>
              <div className="pt-3 border-t border-slate-200 space-y-1">
                {isAuthenticated ? (
                  <>
                    <p className="px-3 py-2 text-sm text-slate-500 truncate">{user?.name || user?.email}</p>
                    <button type="button" onClick={handleLogout} className="w-full text-left px-3 py-2.5 text-slate-700 font-medium rounded-lg hover:bg-slate-50">
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={closeMobileMenu} className="block px-3 py-2.5 text-slate-700 font-medium rounded-lg hover:bg-slate-50">
                      Login
                    </Link>
                    <Link to="/register" onClick={closeMobileMenu} className="block px-3 py-2.5 font-medium rounded-lg bg-slate-800 text-white hover:bg-slate-700">
                      Sign up
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>
      <main className="flex-grow">{children}</main>
    </div>
  );
}
